/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { API_ORIGIN } from './apiBase';
import { apiRequest } from './apiClient';

const TOKEN_KEY = 'udyam_account_token';
export const ACCOUNT_TOKEN_KEY = TOKEN_KEY;

export class AccountAuthError extends Error {
}

export function getAccountToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem(TOKEN_KEY);
}
function setSession(token) {
    localStorage.setItem(TOKEN_KEY, token);
}
export function clearAccountSession() {
    localStorage.removeItem(TOKEN_KEY);
}

async function handleResponse(res) {
    if (res.status === 401) {
        clearAccountSession();
        throw new AccountAuthError('Session expired. Please sign in again.');
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.message || data.error || `Request failed (${res.status})`);
        // Preserves the { code: 'ACCOUNT_NOT_VERIFIED' } body AccountsService
        // attaches so the login form can switch straight to the OTP step
        // instead of just showing a generic error string.
        err.code = data.code;
        throw err;
    }
    return data;
}

export async function accountSignup(payload) {
    const res = await fetch(`${API_ORIGIN}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    return handleResponse(res);
}

export async function accountVerifyOtp(email, otp) {
    const res = await fetch(`${API_ORIGIN}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
    });
    const data = await handleResponse(res);
    setSession(data.token);
    return data;
}

export async function accountResendOtp(email) {
    const res = await fetch(`${API_ORIGIN}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
    });
    return handleResponse(res);
}

export async function accountLogin(email, password) {
    const res = await fetch(`${API_ORIGIN}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
    });
    const data = await handleResponse(res);
    setSession(data.token);
    return data;
}

export async function accountForgotPassword(email) {
    const res = await fetch(`${API_ORIGIN}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
    });
    return handleResponse(res);
}

export async function accountSetPassword(token, password) {
    const res = await fetch(`${API_ORIGIN}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
    });
    const data = await handleResponse(res);
    setSession(data.token);
    return data;
}

export function accountLogout() {
    clearAccountSession();
}

export async function fetchAccountMe() {
    const token = getAccountToken();
    const res = await fetch(`${API_ORIGIN}/api/auth/me`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    });
    return handleResponse(res);
}
// Real, authenticated buyer/supplier conversation view (Phase 2 of the
// Inquiry/Lead Management System) — only ever shows leads this account is
// actually a party to, and only once an admin has approved the first
// contact (see InquiriesService.findMine on the backend).
export async function accountRequest(path, options = {}) {
    const token = getAccountToken();
    try {
        return await apiRequest(`/api/account${path}`, { ...options, token });
    }
    catch (err) {
        if (err.status === 401) {
            clearAccountSession();
            throw new AccountAuthError('Session expired. Please sign in again.');
        }
        throw err;
    }
}

// Authenticated endpoints that live outside /api/account (AI workspace
// services, for example) use the same session and failure semantics.
export async function authenticatedRequest(path, options = {}) {
    const token = getAccountToken();
    try { return await apiRequest(path, { ...options, token }); }
    catch (err) {
        if (err.status === 401) {
            clearAccountSession();
            throw new AccountAuthError('Session expired. Please sign in again.');
        }
        throw err;
    }
}
export const fetchMyInquiries = () => accountRequest('/inquiries');
export const fetchInquiryThread = (id) => accountRequest(`/inquiries/${id}/messages`);
export const sendInquiryMessage = (id, message) => accountRequest(`/inquiries/${id}/messages`, { method: 'POST', body: JSON.stringify({ message }) });
// Supplier-only self-service pipeline stage — a buyer gets a 403 if they try.
export const updateMyInquiryPipeline = (id, pipelineStatus) => accountRequest(`/inquiries/${id}/pipeline`, { method: 'PUT', body: JSON.stringify({ pipelineStatus }) });
